import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { toast } from "sonner";
import { ApiError } from "@/api/client";
import {
  addChampNote,
  blockChampLogin,
  CHAMP_HAS_NO_TICKET,
  champKeys,
  getChampActivity,
  getChampCalls,
  getChampCustomers,
  getChampJobs,
  getChampProfile,
  getChampTickets,
  revealChampPhone,
  unblockChampLogin,
  type ChampActivityParams,
} from "@/api/champs";
import { ticketKeys } from "@/api/tickets";
import type { CustomerCallsParams, CustomerJobsParams } from "@/api/customers";
import { describeError } from "@/lib/errors";

/**
 * Server state for the champ panel and the full champ page. A 403 or 404 is an
 * answer (not your screen / no such champ), never retried.
 */
const noRetryOnAnswer = (failureCount: number, error: unknown): boolean => {
  if (error instanceof ApiError && (error.status === 403 || error.status === 404)) {
    return false;
  }
  return failureCount < 2;
};

export function useChampProfile(id: number) {
  return useQuery({
    queryKey: champKeys.profile(id),
    queryFn: ({ signal }) => getChampProfile(id, signal),
    enabled: id > 0,
    retry: noRetryOnAnswer,
    staleTime: 30_000,
  });
}

/** Newest page first; "Load earlier" fetches the page before the oldest shown. */
export function useChampActivity(id: number, params: ChampActivityParams = {}) {
  return useInfiniteQuery({
    queryKey: champKeys.activity(id, params),
    queryFn: ({ pageParam, signal }) => getChampActivity(id, params, pageParam, signal),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    enabled: id > 0,
    retry: noRetryOnAnswer,
    staleTime: 15_000,
  });
}

export function useChampJobs(
  id: number,
  params: Pick<CustomerJobsParams, "from" | "to">,
) {
  return useQuery({
    queryKey: champKeys.jobs(id, params),
    queryFn: ({ signal }) => getChampJobs(id, params, signal),
    enabled: id > 0,
    retry: noRetryOnAnswer,
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  });
}

export function useChampCustomers(id: number) {
  return useQuery({
    queryKey: champKeys.customers(id),
    queryFn: ({ signal }) => getChampCustomers(id, signal),
    enabled: id > 0,
    retry: noRetryOnAnswer,
    staleTime: 60_000,
  });
}

export function useChampTickets(id: number, enabled = true) {
  return useQuery({
    queryKey: champKeys.tickets(id),
    queryFn: ({ signal }) => getChampTickets(id, signal),
    enabled: enabled && id > 0,
    retry: noRetryOnAnswer,
    staleTime: 10_000,
    refetchInterval: 30_000,
  });
}

export function useChampCalls(id: number, params: CustomerCallsParams, enabled = true) {
  return useQuery({
    queryKey: champKeys.calls(id, params),
    queryFn: ({ signal }) => getChampCalls(id, params, signal),
    enabled: enabled && id > 0,
    retry: noRetryOnAnswer,
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  });
}

/** The server's words for a refusal are written for people; use them. */
export function describeChampError(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 404) return "That champ no longer exists.";
    if (error.status === 409 && error.code === CHAMP_HAS_NO_TICKET) {
      return "No ticket of his own yet — notes can be added once he has one.";
    }
  }
  return describeError(error);
}

/**
 * After a note: the profile's latest three, every timeline of that champ, and
 * the ticket module — the note is an entry on one of his tickets, so an open
 * ticket's conversation, the lists and the champ's ticket tab must show it too.
 */
function useRefreshAfterNote(champId: number) {
  const queryClient = useQueryClient();
  return () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: champKeys.profile(champId) }),
      queryClient.invalidateQueries({ queryKey: champKeys.activities(champId) }),
      queryClient.invalidateQueries({ queryKey: champKeys.tickets(champId) }),
      queryClient.invalidateQueries({ queryKey: ticketKeys.all }),
    ]);
}

export function useAddChampNote(champId: number) {
  const refresh = useRefreshAfterNote(champId);
  return useMutation({
    mutationFn: (input: { text: string }) => addChampNote(champId, input),
    onSuccess: () => toast.success("Note saved"),
    onError: (error) => toast.error(describeChampError(error)),
    onSettled: refresh,
  });
}

/**
 * The whole numbers. A mutation, not a query: the result is held by the panel
 * that asked and never enters the query cache.
 */
export function useRevealChampPhone(champId: number) {
  return useMutation({
    mutationFn: () => revealChampPhone(champId),
    onError: (error) => toast.error(describeChampError(error)),
  });
}

/** Block or unblock the champ's login to the Champ App; refreshes the profile. */
export function useChampLoginBlock(champId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (block: boolean) =>
      block ? blockChampLogin(champId) : unblockChampLogin(champId),
    onSuccess: (_result, block) =>
      toast.success(block ? "Login blocked" : "Login unblocked"),
    onError: (error) => toast.error(describeChampError(error)),
    onSettled: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: champKeys.profile(champId) }),
        queryClient.invalidateQueries({ queryKey: champKeys.activities(champId) }),
      ]),
  });
}
